import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {Observable} from 'rxjs/Observable';

import * as actionsUi from './kontakte.actions.ui';
import {
    PageState
} from './kontakte.reducer';
import {filter, flatMap, map, withLatestFrom} from 'rxjs/operators';
import {
    GetFunktionsbereichKontakteAction, GetKontaktDetailAction, GetKontaktTelefoneAction, PostKontaktAktivSetzenAction, PostKontaktAktualisierenAction, PostKontaktAnlegenAction,
    PostKontaktEntfernenAction, PostKontaktExternSetzenAction, PostKontaktTelefonAktualisierenAction, PostKontaktTelefonEntfernenAction, PostKontaktTelefonHinzufuegenAction
} from '../../../client-ngrx/kontaktverwaltung';
import {
    AKTUELLEN_KONTAKT_SETZEN, AktuellenKontaktSetzenAction, EXTERNER_KONTAKT_TOGGLE, ExternerKontaktToggleAction, KONTAKT_AKTIV_TOGGLE, KONTAKT_DETAILS_LADEN,
    KONTAKT_LOESCHEN, KONTAKT_SPEICHERN, KontaktAktivToggleAction, KontaktDetailsLadenAction, KontaktLoeschenAction, KontaktSpeichernAction, TELEFONNUMMER_LOESCHEN, TELEFONNUMMER_SPEICHERN,
    TelefonnummerLoeschenAction, TelefonnummerSpeichernAction
} from './kontakte.actions.ui';
import {getAktuellerKontakt, getAktuelleTelefonnummer, getKontaktDetail} from './kontakte.selector';
import {KontaktBooleanRequest, KontaktDataRequest, KontaktRequest, KontaktTelefonDataRequest, KontaktTelefonRequest} from '../../../clients/kontaktverwaltung';
import {UuidService} from '@orga-app/providers/uuid.service';

@Injectable()
export class KontaktePageUiEffects {

    @Effect()
    pageReset$: Observable<Action> = this.actions$.pipe(
        ofType(actionsUi.PAGE_STATE_RESET)
        , map((x) => {
            return new GetFunktionsbereichKontakteAction();
        })
    );

    @Effect()
    funktionsbereichKontakteLaden$: Observable<Action> = this.actions$.pipe(
        ofType(actionsUi.FUNKTIONSBEREICH_KONTAKTE_LADEN)
        , map((x) => {
            return new GetFunktionsbereichKontakteAction();
        })
    );

    @Effect()
    aktuellenKontaktSetzen$: Observable<Action> = this.actions$.pipe(
        ofType<AktuellenKontaktSetzenAction>(AKTUELLEN_KONTAKT_SETZEN)
        , flatMap((x) => {
            return [
                new GetKontaktTelefoneAction(x.payload.kontakt_id),
                new GetKontaktDetailAction(x.payload.kontakt_id)
                ];
        })
    );

    @Effect()
    kontaktDetailsLaden$: Observable<Action> = this.actions$.pipe(
        ofType<KontaktDetailsLadenAction>(KONTAKT_DETAILS_LADEN)
        , withLatestFrom(this.store$.pipe(select(getKontaktDetail)))
        , filter(([x, detail]) => detail !== null && detail !== undefined && detail.kontakt_id !== null)
        , map(([x, detail]) => {
            return new GetKontaktDetailAction(detail.kontakt_id);
        })
    );

    @Effect()
    kontaktSpeichern$: Observable<Action> = this.actions$.pipe(
        ofType<KontaktSpeichernAction>(KONTAKT_SPEICHERN)
        , withLatestFrom(this.store$.pipe(select(getKontaktDetail)))
        , filter(([x, kontakt]) => kontakt !== null && kontakt !== undefined)
        , map(([x, kontakt]) => {
            if (kontakt.kontakt_id === null) {
                kontakt = Object.assign({}, kontakt, {
                    kontakt_id: this.uuid.generateUUID()
                });
                return new PostKontaktAnlegenAction({
                    kontakt_id: kontakt.kontakt_id,
                    funktionsbereich_id: kontakt.funktionsbereich_id,
                    beschreibung: kontakt.beschreibung,
                    email: kontakt.email,
                    firma: kontakt.firma,
                    name: kontakt.name,
                    vorname: kontakt.vorname
                } as KontaktDataRequest);
            } else {
                return new PostKontaktAktualisierenAction({
                    kontakt_id: kontakt.kontakt_id,
                    funktionsbereich_id: kontakt.funktionsbereich_id,
                    beschreibung: kontakt.beschreibung,
                    email: kontakt.email,
                    firma: kontakt.firma,
                    name: kontakt.name,
                    vorname: kontakt.vorname
                } as KontaktDataRequest);
            }
        }));

    @Effect()
    kontaktLoeschen$: Observable<Action> = this.actions$.pipe(
        ofType<KontaktLoeschenAction>(KONTAKT_LOESCHEN)
        , withLatestFrom(this.store$.pipe(select(getAktuellerKontakt)))
        , filter(([x, kontakt]) => kontakt !== null && kontakt !== undefined)
        , map(([x, kontakt]) => {
            return new PostKontaktEntfernenAction({
                kontakt_id: kontakt.kontakt_id,
                funktionsbereich_id: kontakt.funktionsbereich_id
            } as KontaktRequest);
        }));

    @Effect()
    externerKontaktToggle$: Observable<Action> = this.actions$.pipe(
        ofType<ExternerKontaktToggleAction>(EXTERNER_KONTAKT_TOGGLE)
        , withLatestFrom(this.store$.pipe(select(getAktuellerKontakt)))
        , filter(([x, kontakt]) => kontakt !== null && kontakt !== undefined)
        , map(([x, kontakt]) => {
            return new PostKontaktExternSetzenAction({
                kontakt_id: kontakt.kontakt_id,
                funktionsbereich_id: kontakt.funktionsbereich_id,
                wert: x.payload
            } as KontaktBooleanRequest);
        }));

    @Effect()
    kontaktAktivToggle$: Observable<Action> = this.actions$.pipe(
        ofType<KontaktAktivToggleAction>(KONTAKT_AKTIV_TOGGLE)
        , withLatestFrom(this.store$.pipe(select(getAktuellerKontakt)))
        , filter(([x, kontakt]) => kontakt !== null && kontakt !== undefined)
        , map(([x, kontakt]) => {
            return new PostKontaktAktivSetzenAction({
                kontakt_id: kontakt.kontakt_id,
                funktionsbereich_id: kontakt.funktionsbereich_id,
                wert: x.payload
            } as KontaktBooleanRequest);
        }));

    @Effect()
    telefonSpeichern$: Observable<Action> = this.actions$.pipe(
        ofType<TelefonnummerSpeichernAction>(TELEFONNUMMER_SPEICHERN)
        , withLatestFrom(this.store$.pipe(select(getAktuelleTelefonnummer)))
        , filter(([x, telefon]) => telefon !== null && telefon !== undefined)
        , map(([x, telefon]) => {
            if (telefon.kontakt_telefon_id === null) {
                return new PostKontaktTelefonHinzufuegenAction({
                    kontakt_id: telefon.kontakt_id,
                    telefon: telefon.telefon,
                    kontakt_telefon_id: this.uuid.generateUUID(),
                    label: telefon.label
                } as KontaktTelefonDataRequest);
            } else {
                return new PostKontaktTelefonAktualisierenAction({
                    kontakt_id: telefon.kontakt_id,
                    kontakt_telefon_id: telefon.kontakt_telefon_id,
                    telefon: telefon.telefon,
                    label: telefon.label
                } as KontaktTelefonDataRequest);
            }
        }));

    @Effect()
    telefonnummerLoeschen$: Observable<Action> = this.actions$.pipe(
        ofType<TelefonnummerLoeschenAction>(TELEFONNUMMER_LOESCHEN)
        , withLatestFrom(this.store$.pipe(select(getAktuelleTelefonnummer)))
        , filter(([x, telefonnummer]) => telefonnummer !== null && telefonnummer !== undefined)
        , map(([x, telefonnummer]) => {
            return new PostKontaktTelefonEntfernenAction({
                kontakt_id: telefonnummer.kontakt_id,
                kontakt_telefon_id: telefonnummer.kontakt_telefon_id
            } as KontaktTelefonRequest);
        }));

    constructor(private actions$: Actions,
                private store$: Store<PageState>,
                private uuid: UuidService) {
    }
}
